import {
    LeaderboardEntry,
    LeaderboardPageResponse,
    RankedLeaderboardEntry,
} from "@/src/models/leaderboard.model";
import { BlocksUser } from "@/src/models/user.model";
import { decodeToken } from "@/src/services/api/auth.api";
import gqlClient from "@/src/services/api/graphql.client";
import { fetchBlocksUsersByIds } from "@/src/services/users.blocks.api";
import {
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
    GET_ALL_LEADERBOARDS,
    GET_LEADERBOARD_BY_USER,
    INSERT_LEADERBOARD,
    UPDATE_LEADERBOARD,
    UPDATE_LEADERBOARD_PERIOD,
    UPDATE_LEADERBOARD_POINTS_ONLY,
    UPDATE_LEADERBOARD_RESET,
} from "./leaderboard.query";

export const LEADERBOARD_QUERY_KEYS = {
  base: ["leaderboard"] as const,
  byUser: (userId: string) => ["leaderboard", "user", userId] as const,
  all: (pageNo?: number) => ["leaderboard", "all", pageNo] as const,
  ranked: () => ["leaderboard", "ranked"] as const,
};

const LEADERBOARD_PAGE_SIZE = 10;

/** Current season key, e.g. "2026-07" (local calendar month). */
export const getCurrentPeriod = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const PERIOD_RE = /^\d{4}-\d{2}$/;
const isValidPeriod = (p: unknown): p is string =>
  typeof p === "string" && PERIOD_RE.test(p);

type GetLeaderboardByUserResponse = {
  getleaderboards: {
    items: LeaderboardEntry[];
    totalCount: number;
  };
};

type GetAllLeaderboardsResponse = {
  getleaderboards: LeaderboardPageResponse;
};

export const toRankedEntries = (
  entries: LeaderboardEntry[],
  usersMap: Record<string, BlocksUser>,
): RankedLeaderboardEntry[] =>
  [...entries]
    .sort((a, b) => b.points - a.points)
    .map((entry, index) => {
      const blocksUser = usersMap[entry.CreatedBy];
      return {
        userId: entry.CreatedBy,
        displayName: blocksUser
          ? `${blocksUser.firstName} ${blocksUser.lastName}`.trim()
          : entry.CreatedBy,
        avatar: blocksUser?.profileImageUrl ?? null,
        points: entry.points,
        achievements: entry.achievementCount,
        rank: index + 1,
      };
    });
export const fetchLeaderboardEntryForUser = async (
  userId: string,
): Promise<LeaderboardEntry | null> => {
  const client = await gqlClient();
  const orgId = (await decodeToken())?.org_id;
  const data = await client.request<GetLeaderboardByUserResponse>(
    GET_LEADERBOARD_BY_USER,
    { filter: userId, orgId },
  );
  return data.getleaderboards.items[0] ?? null;
};

export const useGetUserLeaderboardQuery = (
  userId: string,
  options?: { enabled?: boolean },
) =>
  useQuery<LeaderboardEntry | null>({
    queryKey: LEADERBOARD_QUERY_KEYS.byUser(userId),
    queryFn: () => fetchLeaderboardEntryForUser(userId),
    enabled: (options?.enabled ?? true) && !!userId,
  });

export const useGetRankedLeaderboardQuery = (
  pageNo: number = 1,
  pageSize: number = LEADERBOARD_PAGE_SIZE,
) =>
  useQuery<RankedLeaderboardEntry[]>({
    queryKey: LEADERBOARD_QUERY_KEYS.ranked(),
    queryFn: async () => {
      const client = await gqlClient();
      const orgId = (await decodeToken())?.org_id;
      const data = await client.request<GetAllLeaderboardsResponse>(
        GET_ALL_LEADERBOARDS,
        { orgId, pageNo, pageSize },
      );

      const entries = data.getleaderboards.items;
      if (!entries.length) return [];

      const uniqueUserIds = [...new Set(entries.map((e) => e.CreatedBy))];
      const usersMap = await fetchBlocksUsersByIds(uniqueUserIds);
      return toRankedEntries(entries, usersMap);
    },
    staleTime: 1000 * 60 * 2,
  });

export const useGetAllLeaderboardsQuery = (
  pageNo: number = 1,
  pageSize: number = LEADERBOARD_PAGE_SIZE,
) =>
  useQuery<LeaderboardPageResponse>({
    queryKey: LEADERBOARD_QUERY_KEYS.all(pageNo),
    queryFn: async () => {
      const client = await gqlClient();
      const orgId = (await decodeToken())?.org_id;
      const data = await client.request<GetAllLeaderboardsResponse>(
        GET_ALL_LEADERBOARDS,
        { orgId, pageNo, pageSize },
      );
      return data.getleaderboards;
    },
  });

export const useUpsertLeaderboardPoints = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      pointsDelta,
      currentEntry,
    }: {
      userId: string;
      pointsDelta: number;
      currentEntry: LeaderboardEntry | null;
    }) => {
      const client = await gqlClient();
      let entry = currentEntry;

      if (entry?.ItemId === "optimistic") {
        entry = await fetchLeaderboardEntryForUser(userId);
      }

      if (!entry) {
        entry = await fetchLeaderboardEntryForUser(userId);
      }

      if (!entry) {
        const period = getCurrentPeriod();
        const result = await client.request<{
          insertleaderboard: { itemId: string };
        }>(INSERT_LEADERBOARD, {
          points: Math.max(0, pointsDelta),
          achievementCount: 0,
          period,
        });

        const newEntry: LeaderboardEntry = {
          ItemId: result.insertleaderboard.itemId,
          CreatedBy: userId,
          CreatedDate: new Date().toISOString(),
          LastUpdatedDate: new Date().toISOString(),
          points: Math.max(0, pointsDelta),
          achievementCount: 0,
          period,
        };

        queryClient.setQueryData(
          LEADERBOARD_QUERY_KEYS.byUser(userId),
          newEntry,
        );

        return result;
      }

      const newPoints = Math.max(0, entry.points + pointsDelta);
      return client.request(UPDATE_LEADERBOARD_POINTS_ONLY, {
        itemId: entry.ItemId,
        points: newPoints,
      });
    },

    onMutate: async ({ userId, pointsDelta }) => {
      await queryClient.cancelQueries({
        queryKey: LEADERBOARD_QUERY_KEYS.byUser(userId),
      });

      const previousEntry = queryClient.getQueryData<LeaderboardEntry | null>(
        LEADERBOARD_QUERY_KEYS.byUser(userId),
      );

      if (previousEntry && previousEntry.ItemId !== "optimistic") {
        queryClient.setQueryData(
          LEADERBOARD_QUERY_KEYS.byUser(userId),
          (old: LeaderboardEntry | null) =>
            old ? { ...old, points: Math.max(0, old.points + pointsDelta) } : old,
        );

        queryClient.setQueryData(
          LEADERBOARD_QUERY_KEYS.ranked(),
          (old: RankedLeaderboardEntry[] | undefined) => {
            if (!old) return old;
            return old
              .map((e) =>
                e.userId === userId
                  ? { ...e, points: Math.max(0, e.points + pointsDelta) }
                  : e,
              )
              .sort((a, b) => b.points - a.points)
              .map((e, i) => ({ ...e, rank: i + 1 }));
          },
        );
      }

      return { previousEntry };
    },

    onError: (_err, { userId }, context) => {
      if (context?.previousEntry !== undefined) {
        queryClient.setQueryData(
          LEADERBOARD_QUERY_KEYS.byUser(userId),
          context.previousEntry,
        );
      }
    },

    onSettled: (_data, _err, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: LEADERBOARD_QUERY_KEYS.byUser(userId),
      });
      queryClient.invalidateQueries({
        queryKey: LEADERBOARD_QUERY_KEYS.ranked(),
      });
    },
  });
};

/** Fetch every leaderboard entry for the current org, across all pages. */
const fetchAllLeaderboardEntries = async (): Promise<LeaderboardEntry[]> => {
  const client = await gqlClient();
  const orgId = (await decodeToken())?.org_id;
  const all: LeaderboardEntry[] = [];
  let pageNo = 1;
  // Hard cap so a backend paging bug can never spin forever.
  for (let guard = 0; guard < 100; guard++) {
    const data = await client.request<GetAllLeaderboardsResponse>(
      GET_ALL_LEADERBOARDS,
      { orgId, pageNo, pageSize: 100 },
    );
    const page = data.getleaderboards;
    all.push(...page.items);
    if (!page.hasNextPage) break;
    pageNo += 1;
  }
  return all;
};

/**
 * Monthly leaderboard reset. Runs on app launch (see `useMonthlyLeaderboardReset`)
 * and is idempotent across users/devices/launches by reading each entry's
 * `period` (the season its points belong to):
 *
 *   - No entry has a valid period  → first run: stamp everyone into the current
 *     season WITHOUT resetting points or crowning anyone (one-time migration).
 *   - Some entry already shows the current month → the season was already
 *     closed this month: only reset any stragglers left behind, never award.
 *   - The newest period is an earlier month → genuine rollover: crown the top
 *     points holder (achievementCount + 1) and reset everyone to 0.
 *
 * The winner is stamped to the new month first, so a concurrent launch sees the
 * current season already claimed and won't award a second time. The "only crown
 * when someone has points > 0" guard means a duplicate run finds no winner.
 */
export const runMonthlyResetIfDue = async (
  queryClient: QueryClient,
): Promise<void> => {
  const currentPeriod = getCurrentPeriod();
  const entries = await fetchAllLeaderboardEntries();
  if (!entries.length) return;

  const client = await gqlClient();
  const realPeriods = entries.map((e) => e.period).filter(isValidPeriod);

  // First run after `period` was introduced: adopt the current season for every
  // legacy row, keeping their points and awarding nobody.
  if (realPeriods.length === 0) {
    await Promise.all(
      entries.map((entry) =>
        client.request(UPDATE_LEADERBOARD_PERIOD, {
          itemId: entry.ItemId,
          period: currentPeriod,
        }),
      ),
    );
    queryClient.invalidateQueries({ queryKey: LEADERBOARD_QUERY_KEYS.base });
    return;
  }

  const newestPeriod = realPeriods.reduce((a, b) => (a > b ? a : b));
  const behind = entries.filter(
    (e) => !isValidPeriod(e.period) || e.period < currentPeriod,
  );

  // Current season already active. If anyone was left behind a closed season
  // (e.g. a crash mid-reset), just reset them — the award already happened.
  if (newestPeriod >= currentPeriod) {
    if (behind.length) {
      await Promise.all(
        behind.map((entry) =>
          client.request(UPDATE_LEADERBOARD_RESET, {
            itemId: entry.ItemId,
            period: currentPeriod,
          }),
        ),
      );
      queryClient.invalidateQueries({ queryKey: LEADERBOARD_QUERY_KEYS.base });
    }
    return;
  }

  // Genuine rollover: the latest claimed season is an earlier month and nobody
  // has claimed the current one yet. Crown the leader, reset everyone.
  const sorted = [...entries].sort((a, b) => b.points - a.points);
  const top = sorted[0];
  const hasWinner = top && top.points > 0;

  if (hasWinner) {
    // Stamp the winner into the new season first — this claims the month.
    await client.request(UPDATE_LEADERBOARD, {
      itemId: top.ItemId,
      points: 0,
      achievementCount: top.achievementCount + 1,
      period: currentPeriod,
    });
  }

  const rest = hasWinner
    ? entries.filter((e) => e.ItemId !== top.ItemId)
    : entries;
  await Promise.all(
    rest.map((entry) =>
      client.request(UPDATE_LEADERBOARD_RESET, {
        itemId: entry.ItemId,
        period: currentPeriod,
      }),
    ),
  );

  queryClient.invalidateQueries({ queryKey: LEADERBOARD_QUERY_KEYS.base });
};

/**
 * Runs the monthly reset check once per org per app session. Mount this once in
 * the authenticated tree (see LeaderboardSeasonGate); pass the active org so an
 * account/org switch re-checks the new org. Best-effort: failures are swallowed
 * and retried on the next launch.
 */
export const useMonthlyLeaderboardReset = (
  orgId: string | undefined,
): void => {
  const queryClient = useQueryClient();
  const ranForOrg = useRef<string | null>(null);

  useEffect(() => {
    if (!orgId || ranForOrg.current === orgId) return;
    ranForOrg.current = orgId;

    runMonthlyResetIfDue(queryClient).catch(() => {
      // Allow a retry on the next mount/launch if it failed.
      ranForOrg.current = null;
    });
  }, [orgId, queryClient]);
};
